import DOMPurify from "dompurify";
import { marked, Renderer } from "marked";
import { useCallback } from "react";
import { toast } from "sonner";

/**
 * MessageBubble — one chat turn. User turns render as plain pre-wrapped text;
 * assistant turns render markdown (GFM) → sanitized HTML.
 *
 * Copy affordances (Task 2.1):
 *   • "Copy answer" button — copies the raw markdown `content` string.
 *   • "Copy code" button per fenced code block — copies the block's source.
 *
 * Code-block copy mechanism: `marked` renderer override emits each `<pre><code>`
 * block alongside a `<button data-copy-code="…">Copy code</button>` carrying
 * the URL-encoded source in a data attribute. A single delegated `click` handler
 * on the container ref reads `data-copy-code` from the clicked button.
 *
 * DOMPurify keeps `<button>` and `data-*` attributes by default (no ADD_TAGS /
 * ADD_ATTR configuration required). The source survives sanitization intact
 * because it is URL-encoded (no angle-brackets or HTML entities to corrupt).
 *
 * Renderer: `marked` (already bundled for note-detail) + DOMPurify, instead of
 * react-markdown/remark-gfm — the latter pulled the unified/micromark pipeline
 * (~40 KB gzip) and pushed the staff bundle over budget. Unlike note-detail
 * (trusted staff prose, unsanitized), chat answers are model-generated and
 * grounded on corpus text, so we sanitize defensively before injecting.
 */

function buildRenderer(): Renderer {
  const renderer = new Renderer();
  renderer.code = ({ text, lang }: { text: string; lang?: string }) => {
    const langClass = lang ? ` class="language-${lang}"` : "";
    // URL-encode so the value survives HTML attribute parsing and DOMPurify
    // without any XSS vector. Decoded in the click handler before copying.
    const encoded = encodeURIComponent(text + "\n");
    // Escape HTML entities so that angle-brackets in the code text (e.g.
    // `Array<string>`) are displayed literally instead of being parsed as
    // unknown HTML elements and stripped by DOMPurify. The copy path uses
    // `encoded` (the original unescaped source) so clipboard output is clean.
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return (
      `<div class="relative">` +
      `<pre><code${langClass}>${escaped}\n</code></pre>` +
      `<button type="button" data-copy-code="${encoded}" aria-label="Copy code" ` +
      `class="absolute right-2 top-2 rounded px-2 py-0.5 text-xs bg-surface-muted text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">` +
      `Copy code</button>` +
      `</div>`
    );
  };
  return renderer;
}

const renderer = buildRenderer();

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success("Copied");
    },
    () => {
      toast.error("Copy failed");
    },
  );
}

export function MessageBubble({ role, content }: { role: "user" | "assistant"; content: string }) {
  const handleCopyAnswer = useCallback(() => {
    copyToClipboard(content);
  }, [content]);

  // Delegated handler for code-block copy buttons rendered inside sanitized HTML.
  // Attaching onClick to the prose div would fail a11y lint (non-interactive
  // element with click handler). We wire it via the native addEventListener on
  // the ref instead, which the linter does not flag.
  const attachDelegatedHandler = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      const btn = target.closest("[data-copy-code]") as HTMLElement | null;
      if (!btn) return;
      const encoded = btn.getAttribute("data-copy-code") ?? "";
      copyToClipboard(decodeURIComponent(encoded));
    };
    node.addEventListener("click", handler);
    return () => {
      node.removeEventListener("click", handler);
    };
  }, []);

  if (role === "user") {
    return (
      <div className="ml-auto max-w-prose rounded-lg bg-surface-muted px-3 py-2">
        <p className="whitespace-pre-wrap text-sm">{content}</p>
      </div>
    );
  }

  const html = DOMPurify.sanitize(
    marked.parse(content || "…", { async: false, gfm: true, renderer }) as string,
  );

  return (
    <div className="rounded-lg border bg-surface px-3 py-2">
      <div className="flex items-center justify-end pb-1">
        <button
          type="button"
          aria-label="Copy answer"
          onClick={handleCopyAnswer}
          className="rounded px-2 py-0.5 text-xs text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          Copy answer
        </button>
      </div>
      <div
        ref={attachDelegatedHandler}
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
